import { useState } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import type { RecurrenceEditScope } from "@/api/appointments";

/**
 * Calendar — "this occurrence, this and following, or all?" for an edit or a
 * cancel inside a repeating appointment.
 *
 * A new single-purpose component rather than a fork of anything shared: the
 * design system's `ConfirmModal` is two-way, and nothing in the codebase offers
 * a three-way choice. It is deliberately NOT rendered for a one-off
 * appointment — those save and cancel immediately, exactly as they always have.
 *
 * 🔴 The "All" option says out loud how many occurrences it will NOT reach.
 * "All" that quietly means "all except the three you changed individually" is
 * the surprise that choice buys, and a reader who is told the number afterwards
 * has already made the decision.
 *
 * 🔴 That warning is shown for an EDIT and never for a CANCEL, because it is
 * only true of an edit. The server protects an individually-edited occurrence
 * from having its details overwritten; it does NOT protect it from being
 * cancelled — cancelling every occurrence and leaving three of them standing
 * would be the surprise, not the protection. Printing the edit sentence above a
 * cancel would promise the opposite of what the button does.
 */

interface Props {
  open: boolean;
  /** "Edit" or "Cancel" — drives the verb throughout the dialog. */
  action: "edit" | "cancel";
  /** Occurrences already edited on their own, which an "All" choice will not touch. */
  detachedCount: number;
  onChoose: (scope: RecurrenceEditScope) => void;
  onCancel: () => void;
  pending?: boolean;
}

const OPTIONS: { scope: RecurrenceEditScope; label: string; hint: string }[] = [
  {
    scope: "ThisOccurrence",
    label: "This occurrence",
    hint: "Only this meeting. The rest of the series is untouched.",
  },
  {
    scope: "ThisAndFollowing",
    label: "This and following",
    hint: "This meeting and every later one. Earlier ones stay as they are.",
  },
  {
    scope: "All",
    label: "All occurrences",
    hint: "Every meeting from today forward. Past ones stay as a record.",
  },
];

export function RecurrenceScopeDialog({
  open,
  action,
  detachedCount,
  onChoose,
  onCancel,
  pending = false,
}: Props) {
  const [scope, setScope] = useState<RecurrenceEditScope>("ThisOccurrence");
  const verb = action === "cancel" ? "Cancel" : "Save";

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={action === "cancel" ? "Cancel repeating appointment" : "Edit repeating appointment"}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="tertiary" type="button" onClick={onCancel} disabled={pending}>
            Back
          </Button>
          <Button type="button" onClick={() => onChoose(scope)} disabled={pending}>
            {pending ? `${verb}…` : verb}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-3">
        <p className="m-0 text-small text-gray-mid">
          This appointment repeats. Which occurrences should this {action === "cancel" ? "cancel" : "change"} apply to?
        </p>

        <div className="flex flex-col gap-space-2" role="radiogroup" aria-label="Which occurrences">
          {OPTIONS.map((o) => (
            <label
              key={o.scope}
              className="flex items-start gap-space-2 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2 cursor-pointer"
            >
              <input
                type="radio"
                name="recurrence-scope"
                className="mt-[3px]"
                checked={scope === o.scope}
                onChange={() => setScope(o.scope)}
              />
              <span className="flex flex-col">
                <span className="text-small text-onyx">{o.label}</span>
                <span className="text-caption text-gray-mid">{o.hint}</span>
              </span>
            </label>
          ))}
        </div>

        {scope === "All" && action === "edit" && detachedCount > 0 && (
          <p className="m-0 text-caption text-gray-mid" role="status">
            {detachedCount === 1
              ? "1 occurrence you changed individually will keep its own details."
              : `${detachedCount} occurrences you changed individually will keep their own details.`}
          </p>
        )}
      </div>
    </Modal>
  );
}
